import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wrench } from "lucide-react";
import { setWorkflowStatus } from "@/lib/api";
import {
  MAIN_LABELS, CONTRACT_LABELS, AD_LABELS, MAIN_TONE, CONTRACT_TONE, AD_TONE,
} from "@/lib/workflow";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const AUTO = "__auto__";
const FIELDS = [
  ["main", "Main Status", MAIN_LABELS, MAIN_TONE],
  ["contract", "Contract", CONTRACT_LABELS, CONTRACT_TONE],
  ["ad", "Ad Status", AD_LABELS, AD_TONE],
];

export function StatusChips({ ws, compact = false }) {
  if (!ws) return null;
  const base = `inline-flex items-center border rounded-full font-semibold whitespace-nowrap ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"}`;
  return (
    <>
      <span data-testid="chip-main" className={`${base} ${MAIN_TONE[ws.main]}`}>{MAIN_LABELS[ws.main]}</span>
      <span data-testid="chip-contract" className={`${base} ${CONTRACT_TONE[ws.contract]}`}>{CONTRACT_LABELS[ws.contract]}</span>
      <span data-testid="chip-ad" className={`${base} ${AD_TONE[ws.ad]}`}>{compact ? `Ad: ${AD_LABELS[ws.ad].replace("Ad ", "").replace(" in Progress", "")}` : AD_LABELS[ws.ad].startsWith("Ad") ? AD_LABELS[ws.ad] : `Ad ${AD_LABELS[ws.ad]}`}</span>
    </>
  );
}

export function WorkflowStatusPanel({ horse, onChange }) {
  const ws = horse.workflow_status;
  const [busy, setBusy] = useState(false);
  if (!ws) return null;

  const update = async (key, value) => {
    const next = { ...(ws.overrides || {}), [key]: value === AUTO ? null : value };
    setBusy(true);
    try {
      const h = await setWorkflowStatus(horse.id, next);
      onChange(h);
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="workflow-status-panel" className="mb-4 bg-white border border-stone-200 rounded-xl p-3 sm:p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider font-semibold text-stone-500">
        <Wrench className="h-3.5 w-3.5 text-amber-800" /> Workflow Status
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FIELDS.map(([key, label, labels]) => {
          const overridden = !!ws.overrides?.[key];
          return (
            <div key={key}>
              <label className="block text-[11px] text-stone-400 font-semibold mb-1">
                {label} {overridden && <span className="text-amber-800">· manual</span>}
              </label>
              <Select value={overridden ? ws.overrides[key] : AUTO} onValueChange={(v) => update(key, v)} disabled={busy}>
                <SelectTrigger data-testid={`workflow-${key}-select`} className="h-11 sm:h-10 border-stone-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Automatic ({labels[ws.auto[key]]})</SelectItem>
                  {Object.entries(labels).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-stone-400">
        Automatic values follow Smartsheet status, publishing and posting dates. Manual corrections are stored in the app only — never written to Smartsheet.
      </p>
    </div>
  );
}
